import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { functions } from '../../firebase/firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { firebaseCache } from '../../utils/firebaseCache';

/**
 * completeOnboarding thunk
 * Calls the completeOnboarding Cloud Function to save admin details + create staff accounts
 */
export const completeOnboarding = createAsyncThunk(
    'onboarding/completeOnboarding',
    async ({ adminDetails, staffList }, { rejectWithValue }) => {
        try {
            const completeOnboardingFn = httpsCallable(functions, 'completeOnboarding');
            const result = await completeOnboardingFn({ adminDetails, staffList });

            // Invalidate staff cache so the list refreshes
            firebaseCache.markForInvalidation('staffs');

            return result.data;
        } catch (error) {
            console.error('Error completing onboarding:', error);
            return rejectWithValue(error.message || 'Failed to complete onboarding');
        }
    }
);

/**
 * createStaffAccount thunk
 * Calls the createStaffAccount Cloud Function to create a single staff from StaffList page
 */
export const createStaffAccount = createAsyncThunk(
    'onboarding/createStaffAccount',
    async ({ branchName, email, password, whatsappNumber }, { rejectWithValue }) => {
        try {
            const createStaffFn = httpsCallable(functions, 'createStaffAccount');
            const result = await createStaffFn({ branchName, email, password, whatsappNumber });

            // Invalidate staff cache
            firebaseCache.markForInvalidation('staffs');

            return result.data;
        } catch (error) {
            console.error('Error creating staff account:', error);
            return rejectWithValue(error.message || 'Failed to create staff account');
        }
    }
);

/**
 * markOnboardingComplete thunk
 * Calls the backend to set admins/{uid}.onboardingComplete = true
 */
export const markOnboardingComplete = createAsyncThunk(
    'onboarding/markOnboardingComplete',
    async (_, { rejectWithValue }) => {
        try {
            const { auth, db } = await import('../../firebase/firebaseConfig');
            const { doc, updateDoc } = await import('firebase/firestore');
            const user = auth.currentUser;
            if (!user) throw new Error('No authenticated user');

            // Update Firestore directly — admin has write access to their own doc
            const adminRef = doc(db, 'admins', user.uid);
            await updateDoc(adminRef, {
                onboardingComplete: true,
                onboardingCompletedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            });

            console.log('✅ Onboarding marked complete for', user.uid);
            return { success: true };
        } catch (error) {
            console.error('Error marking onboarding complete:', error);
            return rejectWithValue(error.message || 'Failed to mark onboarding complete');
        }
    }
);

const onboardingSlice = createSlice({
    name: 'onboarding',
    initialState: {
        needsOnboarding: false,
        onboardingLoading: false,
        onboardingError: null,
        trialEnd: null,
        trialExpired: false,
        createdStaff: [],
        failedStaff: [],
        createStaffLoading: false,
        createStaffError: null,
    },
    reducers: {
        setNeedsOnboarding: (state, action) => {
            state.needsOnboarding = action.payload;
        },
        setTrialEnd: (state, action) => {
            state.trialEnd = action.payload;
            if (action.payload) {
                state.trialExpired = new Date(action.payload) < new Date();
            }
        },
        clearOnboardingError: (state) => {
            state.onboardingError = null;
        },
        clearCreateStaffError: (state) => {
            state.createStaffError = null;
        },
        resetOnboarding: (state) => {
            state.needsOnboarding = false;
            state.onboardingLoading = false;
            state.onboardingError = null;
            state.createdStaff = [];
            state.failedStaff = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // completeOnboarding
            .addCase(completeOnboarding.pending, (state) => {
                state.onboardingLoading = true;
                state.onboardingError = null;
            })
            .addCase(completeOnboarding.fulfilled, (state, action) => {
                state.onboardingLoading = false;
                state.createdStaff = action.payload.createdStaff || [];
                state.failedStaff = action.payload.failedStaff || [];
                state.trialEnd = action.payload.trialEnd;
            })
            .addCase(completeOnboarding.rejected, (state, action) => {
                state.onboardingLoading = false;
                state.onboardingError = action.payload || 'Failed to complete onboarding';
            })
            // markOnboardingComplete — don't set needsOnboarding here;
            // the OnboardingModal will do it in handleCloseSuccess after showing the success screen
            .addCase(markOnboardingComplete.fulfilled, (state) => {
                // intentionally empty — modal handles state transition
            })
            // createStaffAccount
            .addCase(createStaffAccount.pending, (state) => {
                state.createStaffLoading = true;
                state.createStaffError = null;
            })
            .addCase(createStaffAccount.fulfilled, (state) => {
                state.createStaffLoading = false;
            })
            .addCase(createStaffAccount.rejected, (state, action) => {
                state.createStaffLoading = false;
                state.createStaffError = action.payload || 'Failed to create staff account';
            });
    },
});

export const {
    setNeedsOnboarding,
    setTrialEnd,
    clearOnboardingError,
    clearCreateStaffError,
    resetOnboarding,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
